import { useCallback, useEffect, useRef } from 'react';

import { useParams } from 'react-router-dom';

import useInterval from '@/app/hooks/useInterval';
import { CameraFacing, CameraFacingInterval, createFacingIntervalBuffer } from './facingIntervalBuffer';
import sendCameraFacingIntervals from './sendCameraFacingIntervals';

export const FLUSH_EVERY_MS = 15 * 1000;

/**
 * Records which camera filmed which stretch of a live LVE call and pushes the
 * intervals to the backend in batches (CAD-6872). The rear-camera stretches are
 * the only part that shows the yard, the fence line and the gate, so they are
 * what a reviewer — and the installer working from the recording — needs to
 * find without scrubbing the whole call.
 *
 * Best-effort by design, like the location breadcrumbs beside it: a failed push
 * must never surface to the customer. Pass `facing` as null whenever nothing is
 * being filmed, so a stretch with no frames is not labelled with a camera.
 */
export default function useCameraFacingIntervals(enabled: boolean, facing: CameraFacing | null) {
  const { hash } = useParams();
  const bufferRef = useRef(createFacingIntervalBuffer());
  const facingRef = useRef(facing);
  // Stretches handed to a request that has not answered yet. They stay here
  // until it succeeds, so a call that ends mid-flight still carries them out on
  // the keepalive flush; the backend upserts on the stretch's start, so
  // re-sending one it already stored costs nothing.
  const inFlightRef = useRef<CameraFacingInterval[]>([]);

  useEffect(() => {
    facingRef.current = facing;
    if (!enabled) {
      return;
    }
    bufferRef.current.accept(facing, Date.now());
  }, [enabled, facing]);

  const flush = useCallback((keepalive = false) => {
    if (!hash) {
      return;
    }
    // A periodic flush waits its turn: two in flight at once can be committed
    // out of order, leaving a stretch shorter than it really was. The keepalive
    // flush is the last chance to send anything, so it never waits.
    if (inFlightRef.current.length && !keepalive) {
      return;
    }
    const closed = [...inFlightRef.current, ...bufferRef.current.drain()];
    const open = bufferRef.current.openSnapshot(Date.now());
    const intervals = open ? [...closed, open] : closed;
    if (!intervals.length) {
      return;
    }
    inFlightRef.current = closed;
    void sendCameraFacingIntervals(hash, intervals, { keepalive }).then(sent => {
      if (!sent) {
        bufferRef.current.restore(closed);
      }
      inFlightRef.current = [];
    });
  }, [hash]);

  useInterval(() => flush(), FLUSH_EVERY_MS, enabled);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const flushNow = () => flush(true);
    // A hidden tab is frozen: the camera stops capturing and the periodic flush
    // stops with it, since it rides on requestAnimationFrame. A locked phone
    // mid-walk is the normal interruption here, not an exotic one, and pagehide
    // may never come — so the stretch is closed on the way out and a fresh one
    // opened on the way back, rather than left to swallow the dead gap.
    const boundStretchToVisibility = () => {
      const now = Date.now();
      if (document.visibilityState === 'hidden') {
        bufferRef.current.accept(null, now);
        flushNow();
        return;
      }
      bufferRef.current.accept(facingRef.current, now);
    };
    window.addEventListener('pagehide', flushNow);
    document.addEventListener('visibilitychange', boundStretchToVisibility);
    return () => {
      window.removeEventListener('pagehide', flushNow);
      document.removeEventListener('visibilitychange', boundStretchToVisibility);
      flush(true);
    };
  }, [enabled, flush]);
}
